import { useEffect } from 'react';

/**
 * Attaches a single global tooltip element to <body> that tracks
 * [data-tooltip] elements via event delegation.  Uses position: fixed
 * so the tooltip escapes any overflow: hidden ancestors.
 *
 * Includes viewport-flip (places below instead of above when near the
 * top edge) and horizontal nudging (keeps the tooltip on-screen near
 * left/right edges, with arrow re-anchored to the original target).
 *
 * Call once at app root.
 */
export function useGlobalTooltip(): void {
  useEffect(() => {
    const el = document.createElement('div');
    el.className = 'global-tooltip';

    const text = document.createElement('span');
    text.className = 'global-tooltip-text';
    el.appendChild(text);

    const arrow = document.createElement('span');
    arrow.className = 'global-tooltip-arrow';
    el.appendChild(arrow);

    document.body.appendChild(el);

    let current: HTMLElement | null = null;

    function show(target: HTMLElement) {
      const t = target.getAttribute('data-tooltip');
      if (!t) return;
      current = target;
      text.textContent = t;
      el.classList.add('visible');
      // Position needs to read offsetWidth/Height, so first make it measurable.
      requestAnimationFrame(() => position(target));
    }

    function hide() {
      current = null;
      el.classList.remove('visible');
    }

    function position(target: HTMLElement) {
      const rect = target.getBoundingClientRect();
      const tipW = el.offsetWidth;
      const tipH = el.offsetHeight;
      const vw = window.innerWidth;
      const margin = 12;
      const gap = 8;

      const targetCenter = rect.left + rect.width / 2;

      // Vertical placement: above by default, below if too close to top.
      const placeBelow = rect.top < tipH + gap + 4;
      const top = placeBelow ? rect.bottom + gap : rect.top - gap - tipH;

      // Horizontal placement: centered on target, shifted to stay on-screen.
      let left = targetCenter - tipW / 2;
      if (left < margin) left = margin;
      if (left + tipW > vw - margin) left = vw - margin - tipW;

      el.style.left = `${left}px`;
      el.style.top = `${top}px`;
      el.dataset.placement = placeBelow ? 'below' : 'above';

      // Arrow x relative to tooltip box, clamped so it points at the target.
      const arrowX = Math.max(10, Math.min(tipW - 10, targetCenter - left));
      arrow.style.left = `${arrowX}px`;
    }

    function onEnter(e: Event) {
      const target = (e.target as HTMLElement).closest?.('[data-tooltip]') as HTMLElement | null;
      if (target) show(target);
    }

    function onLeave(e: Event) {
      const target = (e.target as HTMLElement).closest?.('[data-tooltip]') as HTMLElement | null;
      const related = (e as MouseEvent).relatedTarget as HTMLElement | null;
      if (target && !target.contains(related)) hide();
    }

    function onScroll() {
      if (current) hide();
    }

    document.addEventListener('mouseenter', onEnter, true);
    document.addEventListener('mouseleave', onLeave, true);
    window.addEventListener('scroll', onScroll, true);

    return () => {
      document.removeEventListener('mouseenter', onEnter, true);
      document.removeEventListener('mouseleave', onLeave, true);
      window.removeEventListener('scroll', onScroll, true);
      el.remove();
    };
  }, []);
}
